import { EditType } from 'src/types/edit';
import { InsightSummaryPage } from 'src/types/pages/insights';

export const WORK_LIFE_BALANCE: InsightSummaryPage = {
  page_id: 'insights-18',
  section_order: ['header', 'summary'],
  header: {
    visible: true,
    content: {
      title: 'Work-Life Balance',
      body: 'In the course of adjusting to pandemic-era work, mediators discuss the complicated and sometimes surprising results of working from home.',
      header_type: 'full',
    },
  },
  summary: {
    visible: true,
    content: {
      highlight_link_style: {
        default_color: '#11304B',
        active_color: '#48bf53',
      },
      paragraphs: [
        {
          label: 'Overview & Recommendations',
          text: 'Developing a work-life balance in this season of change has proven to be a process of discovery for members of the agency.  For many individuals it has been a <Embed getEmbedInfo={getEmbedInfo}>double-edged sword-5249391</Embed>. On one hand, the remote working experience has afforded them more time for self-care and opportunities to share quality time with loved ones.  The increased opportunity for quality time is partly due to the drastic reduction in <Embed getEmbedInfo={getEmbedInfo}>“windshield time”-5249068</Embed> traveling to/from meetings. On the flip side, being more accessible to clients via remote meeting technology, there is the challenging temptation to overschedule which can lead to fatigue and burnout.<br/><br/>Recommendations from conversations include:<br/><ul><li>Field operations management provides clear and <Embed getEmbedInfo={getEmbedInfo}>consistent direction-5250477</Embed> to the field regarding creation & maintenance of a healthy meeting, work, and training calendar to give better direction and support the diverse and dynamic community of mediators.</li><li>Field operations management performs regularly scheduled <Embed getEmbedInfo={getEmbedInfo}>“wellness” checks-5250464</Embed> with each mediator to ensure a reasonable work/life balance and determine if the mediators are stretching themselves too thin by over-scheduling.</li><li>Field operations management provides clear <Embed getEmbedInfo={getEmbedInfo}>guidance and coaching-5249446</Embed> on the decision elements to be considered when deciding whether to schedule client services virtually, in-person or in a hybrid environment.</li><li>Within the parameters of the direction and guidance provided, field operations management <Embed getEmbedInfo={getEmbedInfo}>empowers the mediators-5250368</Embed> to set healthy work/life boundaries, assess the needs of clients, work with their areas of strengths, but also stretch and grow in new training.</li></ul>',
        },
        {
          label: 'Scheduling',
          text: 'With the unprecedented elimination of travel for much of the FMCS workforce, discussions surrounding work-life balance were upended as mediators found that their regular rituals of road travel became replaced with the challenges of juggling multiple virtual meetings per day.<br/><br/>Prior to the pandemic-era restrictions, mediators carefully booked their time to include strategic pacing and buffering between clients. That is until, as <Embed getEmbedInfo={getEmbedInfo}>Speaker TB-5249422</Embed> pointed out, “the shock of it wore off for everyone and everyone just jumped into the work, it was three meetings a day and they were usually back-to-back.”<br/><br/>Participants like <Embed getEmbedInfo={getEmbedInfo}>Speaker RA-5249391</Embed> marveled at the sheer number of people and clients they are able to connect with all over the country on any given day; yet at the same time, mediators found themselves physically and mentally exhausted: “I found I was getting pretty well overwhelmed on two or even three meetings a day.” This intensive scheduling would be unimaginable in the time before Covid quarantines but as <Embed getEmbedInfo={getEmbedInfo}>Speaker KC-5249022</Embed> observes regarding online client service, “…once people got comfortable with it, it was just nonstop.”',
        },
        {
          label: 'Travel',
          text: "Across the board, participants like <Embed getEmbedInfo={getEmbedInfo}>Speaker JB-5249326</Embed>, <Embed getEmbedInfo={getEmbedInfo}>Speaker AC-5249109</Embed>, <Embed getEmbedInfo={getEmbedInfo}>Speaker EA-5248122</Embed>, <Embed getEmbedInfo={getEmbedInfo}>Speaker MB-5248125</Embed>, <Embed getEmbedInfo={getEmbedInfo}>Speaker TD-5250601</Embed> and <Embed getEmbedInfo={getEmbedInfo}>Speaker BE-5249122</Embed> recognize and appreciate the flexibility they are now afforded due to the cutting back of travel requirements. <Embed getEmbedInfo={getEmbedInfo}>Speaker WB-5249126</Embed> describes the distinct advantage and alleviated pressures of less travel “I do like the fact that at 9:00 at night, when I get done with a meeting, I don’t have to drive two hours to go home, I can walk 50 feet to the kitchen and get something to eat.” <br/><br/>With some trepidation, some mediators anticipate a return to regular travel time in the near future. As <Embed getEmbedInfo={getEmbedInfo}>Speaker AB-5249126</Embed> observes, once the agency reverts to their standard schedules, “I’m going to be leaving home on a Monday and not coming home until Friday again.” <br/><br/>However, despite the welcome benefits of reduced travel, a common pattern among mediators is that many missed the commute time that typically follows a client session which they use to review the events of the day. <Embed getEmbedInfo={getEmbedInfo}>Speaker VA-5249431</Embed> and <Embed getEmbedInfo={getEmbedInfo}>Speaker SC-5249068</Embed> dub this period of quiet contemplation as “windshield time” and without it, <Embed getEmbedInfo={getEmbedInfo}>Speaker TB-5249422</Embed> says “there’s not a lot of time to reflect.” <br/><br/><Embed getEmbedInfo={getEmbedInfo}>Speaker CD-5250598</Embed> describes the loss of this “decompression time” where “you do miss something, and I miss that time you leave a mediation, you sit in the car, whether it's traffic or you're going down the expressway as fast as you can, and you just let everything that happened in that meeting sink in and you can digest it.”",
        },
        {
          label: 'Wellness',
          text: "Mediators focused on meeting the needs of their clients while caring for their families and maintaining their own physical and emotional wellbeing. Several participants discussed the management of virtual fatigue. <Embed getEmbedInfo={getEmbedInfo}>Speaker LB-5250386</Embed> detailed their concerted efforts to care for their wellbeing noting that, “I’ve never experienced anything like that before…I think within four to five months, I felt a significant drop in my energy level, especially if the negotiation was protracted and was going on for multiple hours, multiple days in a row.” <br/><br/>Mediators also discussed taking steps to care for themselves and have found that with mindful attention, many found ways to decompress. Participants like <Embed getEmbedInfo={getEmbedInfo}>Speaker AB-5250587</Embed> and <Embed getEmbedInfo={getEmbedInfo}>Speaker VA-5249393</Embed> emphasized that it was important to focus on their physical wellness, and like <Embed getEmbedInfo={getEmbedInfo}>Speaker SB-5249056</Embed> pointed out, “I've actually lost quite a few pounds because I've been able to walk more after work.” <br/><br/><Embed getEmbedInfo={getEmbedInfo}>Speaker TC-524944</Embed> recognized that it is up to the individual to prioritize self-care: “Because you’re working at home, you can just get up and work and you can work all evening if you want, work on the weekends. You can, but is it healthy?”",
        },
        {
          label: 'Family',
          text: 'For many mediators, a significant shift in their work life has resulted in a net positive change for family life. However, the initial struggles of coordinating school-age children and partner work schedules were occasionally quite daunting. Stories about these challenges, as well as how participants discovered strategies with bringing their work home are described by <Embed getEmbedInfo={getEmbedInfo}>Speaker CD-5250596</Embed>, who eventually balanced their work tasks with the need to spend more time with loved ones. <br/><br/><Embed getEmbedInfo={getEmbedInfo}>Speaker DA-5249062</Embed> describes it simply, “...the less time commuting has freed up more time with family.”',
        },
      ],
    },
  },
  edit_metadata: {
    page_label: 'Work-Life Balance',
    page_id: 'insights-18',
    subroute: true,
    sections: [
      {
        section_label: 'Introduction',
        edit_blocks: [
          {
            block_label: 'Header',
            match: 'header.content',
            type: EditType.banner,
          },
        ],
      },
      {
        section_label: 'Highlight Link Colors',
        edit_blocks: [
          {
            block_label: 'Color',
            match: 'summary.content.highlight_link_style.default_color',
            type: EditType.color,
          },
          {
            block_label: 'Active Color',
            match: 'summary.content.highlight_link_style.active_color',
            type: EditType.color,
          },
        ],
      },
      {
        section_label: 'Paragraphs',
        update_section: {
          match: 'summary.content.paragraphs',
          label: 'Add Paragraph',
          empty: {
            label: 'Default Label',
            text: 'Default Text',
          },
          newSectionMetadata: {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: `summary.content.paragraphs[index].label`,
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: `summary.content.paragraphs[index].text`,
                type: EditType.embed_string,
              },
            ],
          },
        },
        sub_sections: [
          {
            section_label: 'Overview',
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[0].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[0].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[1].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[1].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[2].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[2].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[3].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[3].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[4].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[4].text',
                type: EditType.embed_string,
              },
            ],
          },
        ],
      },
    ],
  },
};
